import * as Yup from 'yup';

import type { Contexts } from '@sentry/types';
import { Fragment } from 'react';
import { captureException } from '@sentry/react';
import { enqueueSnackbar } from 'notistack';

export class ConvergeFrontendError extends Error {}

export class ConvergeFrontendValidationError extends ConvergeFrontendError {
  constructor(message: string, public yupError?: Yup.ValidationError) {
    super(message);
    this.name = 'ConvergeFrontendValidationError';
  }
}

const IS_NODE_ENV_DEVELOPMENT = process.env['NODE_ENV'] === 'development';
const SHOW_DEV_ERRORS =
  process.env['REACT_APP_SHOW_DEV_ERRORS']?.startsWith('t') ||
  process.env['REACT_APP_SHOW_DEV_ERRORS'] === '1';

export const handleError = ({
  source,
  message,
  cause = new Error(message),
  context = {},
  notify = false,
  throwError,
  fingerprint,
}: {
  source: string;
  message: string;
  cause?: Error | unknown;
  context?: Contexts;
  notify?: boolean;
  throwError?: boolean;
  fingerprint?: string[];
}) => {
  const error = cause instanceof Error ? cause : new Error(message);

  const notificationMessage = `${message}`;

  if (
    typeof window !== 'undefined' &&
    (notify || (IS_NODE_ENV_DEVELOPMENT && SHOW_DEV_ERRORS))
  ) {
    const shortenedNotificationMessage =
      !IS_NODE_ENV_DEVELOPMENT && notificationMessage.length > 120
        ? `${notificationMessage.slice(0, 120)}...`
        : notificationMessage;

    const messageComponent = (
      <div
        className="message-component"
        style={{
          maxHeight: '190',
          maxWidth: '50vm',
          overflow: 'auto',
          overflowWrap: 'break-word',
        }}
      >
        {IS_NODE_ENV_DEVELOPMENT && (
          <strong>[Development {!notify && 'only'} error] </strong>
        )}
        {shortenedNotificationMessage.split('\n').map((line, index) => (
          <Fragment key={index}>
            {line}
            <br />
          </Fragment>
        ))}
      </div>
    );

    enqueueSnackbar({
      message: messageComponent,
      variant: 'error',
      autoHideDuration: 7 * 1000,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
    });
  }

  console.error(notificationMessage, context);

  console.error(cause);

  captureException(cause, {
    fingerprint,
    contexts: {
      _metadata: {
        source,
        notificationSentToUser: notify,
      },
      ...context,
    },
  });

  if (throwError) {
    throw cause;
  }

  return error;
};

export const handleValidationErrors = ({
  message = 'Invalid data',
  source,
  cause,
  inputData,
  throwError,
}: {
  message?: string;
  source: string;
  cause: unknown;
  inputData?: Record<string, unknown> | unknown[];
  throwError?: boolean;
}) => {
  if (cause instanceof Yup.ValidationError) {
    const errorMessage = `${message} (${cause.message}):\n${cause.inner
      .map(
        (error) =>
          `∙ ${
            error.path && !error.message.includes(error.path)
              ? [error.type, error.path].filter(Boolean).join(' ')
              : error.type
              ? error.type
              : 'error'
          }: ${error.message}`
      )
      .join('\n')}`;
    console.error(errorMessage, inputData);

    const convergeValidationError = new ConvergeFrontendValidationError(
      errorMessage,
      cause
    );

    const error = handleError({
      message: errorMessage,
      cause: convergeValidationError,
      source,
      context: {
        ...cause.inner.reduce(
          (errors, error) => ({
            ...errors,
            [error.path ?? 'unknown path']: {
              message: error.message,
              value: error.value,
            },
          }),
          {}
        ),
        invalidData: {
          data: JSON.stringify(inputData),
        },
      },
    });

    if (throwError) {
      throw cause.inner[0] ?? cause;
    }

    return error;
  }

  return handleError({
    message: `Unexpected error during validation: ${message}: ${cause}`,
    cause: cause instanceof Error ? cause : new Error(message),
    source,
    context: {
      invalidData: {
        data: JSON.stringify(inputData),
      },
    },
  });
};
